import type { AsyncState } from '../../../platform/async/Async'
import type { LocalDate } from '../../../platform/localDate'
import type { Cashflow, Period } from '../../cashflow/cashflowWire'

/** A calendar month's server bucket, or null where the server returned none (or it is still to come). */
export type MonthSlot = Period | null

export type ChartView = {
  currency: string
  /** The calendar year of `today`. Last year is `year - 1`. */
  year: number
  /** Twelve slots, January first. Slots after the current month are null. */
  thisYear: MonthSlot[]
  lastYear: MonthSlot[]
}

const MONTHS = 12

/** Twelve slots for `year`: each is the server's own bucket for that month, never a computed one. */
function slots(periods: readonly Period[], year: number, lastMonth: number): MonthSlot[] {
  const out: MonthSlot[] = Array.from({ length: MONTHS }, (): MonthSlot => null)
  for (const p of periods) {
    if (Number(p.start.slice(0, 4)) !== year) continue
    const m = Number(p.start.slice(5, 7))
    if (m >= 1 && m <= lastMonth) out[m - 1] = p
  }
  return out
}

/**
 * The period call (for where history starts) and the history call as the chart's
 * state. The history already spans ten years of monthly buckets, so last year needs
 * no request of its own: both lines are read out of it by calendar month. No
 * transactions, or none in either year, is empty — the chart is never drawn flat.
 * Each failure keeps its own retry, so a failed history does not borrow the period
 * call's.
 */
export function toChartState(period: AsyncState<Cashflow>, history: AsyncState<Cashflow>, today: LocalDate): AsyncState<ChartView> {
  if (period.status === 'loading' || period.status === 'error') return period
  if (period.status === 'empty') return { status: 'empty' }
  if (period.data.window.earliest === null) return { status: 'empty' }
  if (history.status === 'loading' || history.status === 'error') return history
  if (history.status === 'empty') return { status: 'empty' }
  const periods = history.data.periods
  if (periods === null || periods.length === 0) return { status: 'empty' }
  const year = Number(today.slice(0, 4))
  const thisYear = slots(periods, year, Number(today.slice(5, 7)))
  const lastYear = slots(periods, year - 1, MONTHS)
  if (![...thisYear, ...lastYear].some((s) => s !== null)) return { status: 'empty' }
  return { status: 'success', data: { currency: history.data.currency, year, thisYear, lastYear } }
}
